import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
} from "@angular/core";
import { Router, RouterModule, NavigationEnd } from "@angular/router";
import { CommonModule } from "@angular/common";
import {
  TranslationService,
  Language,
} from "../../../../services/translation.service";
import { TranslateDirective } from "../../../../directives/translate.directive";
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from "@angular/forms";
import { AgoraService } from "../../../../../services/agora.service";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { SecureStorageService } from "../../../../../services/secure-storage.service";

import { BrandLogoComponent } from "../widgets/brand-logo/brand-logo.component";
import { FriendRequestComponent } from "../widgets/friend-request/friend-request.component";
import { FeatherIconComponent } from "../../feather-icon/feather-icon.component";
import { SearchBoxComponent } from "../widgets/search-box/search-box.component";
import { MessagesComponent } from "../widgets/messages/messages.component";
import { ModeComponent } from "../widgets/mode/mode.component";
import { MobileMenuComponent } from "../widgets/mobile-menu/mobile-menu.component";
import { UserProfileComponent } from "../widgets/user-profile/user-profile.component";
import { CommonService } from "../../../../services/common.service";
import { ClickOutSideDirective } from "../../../../directives/click-out-side.directive";
import {
  getCurrentUserId,
  setSecureStorageInstance,
} from "../../../../../helpers/utils";
import { EntityService } from "../../../../../services/entity.service";
import { Profil } from "../../../../../models/profil";
import { WebsocketService } from "../../../../../services/websocket.service";
import { Subscription } from "rxjs";
import { filter } from "rxjs/operators";
import { AuthService } from "../../../../../services/auth.service";
import { AuthModalService } from "../../../../services/auth-modal.service";
import { CreateStreamModalComponent } from "../../widgets/create-stream-modal/create-stream-modal.component";
import { environment } from "../../../../../../environments/environment";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { ProfileService } from "../../../../../services/profile.service";
import { HeaderNotificationService } from "../../../../../services/header-notification.service";

@Component({
  selector: "app-header-one",
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    BrandLogoComponent,
    SearchBoxComponent,
    FriendRequestComponent,
    MessagesComponent,
    ModeComponent,
    MobileMenuComponent,
    UserProfileComponent,
    FeatherIconComponent,
    ClickOutSideDirective,
    TranslateDirective,
    ReactiveFormsModule,
  ],
  providers: [WebsocketService],
  templateUrl: "./header-one.component.html",
  styleUrl: "./header-one.component.scss",
  host: {
    '[class.mock-mobile-header]': "mobileLayout === 'mock'",
  },
})
export class HeaderOneComponent implements OnInit, OnDestroy {
  public isShow: boolean = false;
  @Input() currentUrl: string;
  @Input() mobileLayout: 'default' | 'mock' = 'default';
  public isOpen: boolean = false;
  profil: Profil | null = null;
  private routerSubscription: Subscription | null = null;
  isDropdownOpen = false;
  isAuthenticated: boolean = false;
  currentLanguage = "Français";
  isLanguageDropdownOpen = false;
  isCreateStreamModalOpen: boolean = false;
  isCreatingStream: boolean = false;
  streamForm: FormGroup;
  loading = false;

  isMobileScreen = false;
  mobileOffcanvasOpen = false;
  mobileSearchOpen = false;
  isOffcanvasLangOpen = false;
  private mediaQueryList: MediaQueryList | null = null;
  private mediaQueryListener: (() => void) | null = null;

  private baseApiUrl: string = environment.apiUrl;
  private token: string;
  private httpOptions: { headers: HttpHeaders };

  constructor(
    private router: Router,
    private entityService: EntityService,
    public commonServices: CommonService,
    private websocketService: WebsocketService,
    private authService: AuthService,
    private cdRef: ChangeDetectorRef,
    private authModalService: AuthModalService,
    private translationService: TranslationService,
    private formBuilder: FormBuilder,
    private agoraService: AgoraService,
    private modalService: NgbModal,
    private http: HttpClient,
    private profileService: ProfileService,
    private secureStorage: SecureStorageService,
    public headerNotificationService: HeaderNotificationService
  ) {
    this.currentUrl = this.router.url;
    this.streamForm = this.formBuilder.group({
      title: ["", [Validators.required, Validators.minLength(3)]],
    });

    this.token = this.secureStorage.getItem<string>("token") || "";
    this.httpOptions = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.token}`,
      }),
    };

    // Initialiser l'instance de SecureStorageService pour utils.ts
    setSecureStorageInstance(this.secureStorage);
  }

  ngOnInit(): void {
    const token = this.secureStorage.getItem<string>("token");
    this.isAuthenticated = !!token;
    this.cdRef.detectChanges();
    console.log("isAuthenticated", this.isAuthenticated);

    // S'abonner aux changements de route pour mettre à jour currentUrl
    this.routerSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.currentUrl = event.urlAfterRedirects;
        this.closeMobileSearch();
        this.closeMobileOffcanvas();
        this.cdRef.detectChanges();
      });
    if (this.isAuthenticated) {
      const userId = getCurrentUserId();
      this.loadInitialData(userId);
      this.loadUserProfile();

      this.profileService.profile$.subscribe({
        next: (data) => {
          if (data) {
            this.profil = data.data;

            // S'abonner aux notifications après avoir le rôle
            const role = this.profil?.rang as "fan" | "star";
            if (this.profil?.rang) {
              this.secureStorage.setItem(
                "rang",
                String(this.profil.rang).toLowerCase()
              );
            }
            this.headerNotificationService.subscribeToNotifications(
              +userId,
              role
            );

            // Nettoyer les éventuels doublons existants
            this.headerNotificationService.cleanDuplicates();
          }
        },
        error: () => {
          this.profil = null;
        },
      });
    }

    // Initialiser la langue actuelle
    this.translationService.currentLang$.subscribe((lang) => {
      this.currentLanguage = this.translationService.getLanguageName(lang);
    });

    // Détection mobile (max-width: 1024px) pour offcanvas
    if (typeof window !== "undefined" && window.matchMedia) {
      this.mediaQueryList = window.matchMedia("(max-width: 1024px)");
      this.isMobileScreen = this.mediaQueryList.matches;
      this.mediaQueryListener = () => {
        this.isMobileScreen = this.mediaQueryList!.matches;
        if (!this.isMobileScreen) {
          this.mobileOffcanvasOpen = false;
        }
        this.cdRef.detectChanges();
      };
      this.mediaQueryList.addEventListener("change", this.mediaQueryListener);
    }
  }

  private loadInitialData(userId: string) {
    if (!this.isAuthenticated) return;

    this.entityService.getDataById("/pendingRequest", userId).subscribe({
      next: (data: any) => {
        this.headerNotificationService.setInitialFanRequests(data.data || []);
      },
      error: () =>
        this.headerNotificationService.setInitialFanRequests([]),
    });

    this.entityService
      .getDataById("/acceptedUnreadFanVideos", userId)
      .subscribe({
        next: (data: any) => {
          this.headerNotificationService.setInitialAcceptedVideos(
            data.data || []
          );
        },
        error: () =>
          this.headerNotificationService.setInitialAcceptedVideos([]),
      });
  }

  private loadUserProfile() {
    const userId = this.secureStorage.getItem<string>("id");
    if (userId) {
      this.loading = true;
      this.entityService.getDataById<Profil>("/users", userId).subscribe({
        next: (profil: any) => {
          this.profil = profil?.data || profil;
          this.loading = false;
        },
        error: (error) => {
          console.error("Erreur lors du chargement du profil:", error);
          this.loading = false;
        },
      });
    }
  }

  ngOnDestroy() {
    this.routerSubscription?.unsubscribe();
    if (this.mediaQueryList && this.mediaQueryListener) {
      this.mediaQueryList.removeEventListener("change", this.mediaQueryListener);
    }
    this.profileService.resetProfile();
    this.headerNotificationService.reset();
  }

  openMobileOffcanvas(): void {
    this.mobileOffcanvasOpen = true;
    this.cdRef.detectChanges();
  }

  closeMobileOffcanvas(): void {
    this.mobileOffcanvasOpen = false;
    this.closeOffcanvasLanguageDropdown();
    this.cdRef.detectChanges();
  }

  toggleOffcanvasLanguageDropdown(): void {
    this.isOffcanvasLangOpen = !this.isOffcanvasLangOpen;
  }

  closeOffcanvasLanguageDropdown(): void {
    this.isOffcanvasLangOpen = false;
  }

  openMobileSearch(): void {
    this.mobileSearchOpen = true;
    this.cdRef.detectChanges();
  }

  closeMobileSearch(): void {
    this.mobileSearchOpen = false;
    this.cdRef.detectChanges();
  }

  logOut() {
    // Appel à l'API de déconnexion en arrière-plan
    this.http
      .post(`${this.baseApiUrl}/logout`, {}, this.httpOptions)
      .subscribe({
        next: () => {
          console.log("Déconnexion réussie côté serveur");
        },
        error: (error) => {
          console.error("Erreur lors de la déconnexion côté serveur:", error);
        },
      });

    // Continuer avec le processus de déconnexion côté client
    this.secureStorage.removeItem("token");
    this.isAuthenticated = false;
    this.profileService.resetProfile();
    this.router.navigateByUrl("/auth/login");
  }

  outSideClose() {
    this.isOpen = false;
  }

  closeDropdown() {
    this.isDropdownOpen = false;
  }

  handleAuthenticatedNavigation(path: string[]) {
    if (!this.isAuthenticated) {
      this.authModalService.openAuthModal();
      return;
    }
    this.router.navigate(path);
  }

  handleProfileClick() {
    if (!this.isAuthenticated) {
      this.authModalService.openAuthModal();
      return;
    }
    this.isOpen = !this.isOpen;
  }

  toggleLanguageDropdown() {
    this.isLanguageDropdownOpen = !this.isLanguageDropdownOpen;
  }

  closeLanguageDropdown() {
    this.isLanguageDropdownOpen = false;
  }

  changeLanguage(lang: "fr" | "en" | "cd" | "globe") {
    this.translationService.setLanguage(lang);
    this.closeLanguageDropdown();
  }

  getLanguageFlag(language: string): string {
    const langCode = this.getLangCode(language);
    return this.translationService.getLanguageFlag(langCode as Language);
  }

  private getLangCode(language: string): string {
    switch (language) {
      case "Français":
        return "fr";
      case "English":
        return "en";
      case "Lingala":
        return "cd";
      case "Kiswahili":
        return "globe";
      default:
        return "fr";
    }
  }

  // Méthode de traduction à utiliser dans le template
  translate(key: string): string {
    return this.translationService.translate(key);
  }

  // Méthode pour vérifier si un lien est actif
  isLinkActive(path: string | string[]): boolean {
    if (Array.isArray(path)) {
      return path.some((p) => {
        if (p === "/") {
          return this.currentUrl === "/" || this.currentUrl === "";
        }
        return this.currentUrl === p || this.currentUrl.startsWith(p + "/");
      });
    }

    if (path === "/") {
      return this.currentUrl === "/" || this.currentUrl === "";
    }

    return this.currentUrl === path || this.currentUrl.startsWith(path + "/");
  }

  openCreateStreamModal(): void {
    if (!this.isAuthenticated) {
      this.authModalService.openAuthModal();
      return;
    }
    if (this.profil?.rang !== "star") {
      this.router.navigate(["/streams"]);
      return;
    }

    const modalRef = this.modalService.open(CreateStreamModalComponent, {
      centered: true,
      size: "lg",
      backdrop: "static",
      keyboard: false,
    });

    const modalComponent = modalRef.componentInstance;
    modalComponent.profil = this.profil;

    modalComponent.submitForm.subscribe((formData: any) => {
      const data = {
        ...formData,
        description: `Stream de ${
          this.profil?.scene_name || this.profil?.prenom
        }`,
      };

      this.agoraService.createStream(data).subscribe({
        next: (response: any) => {
          console.log("response stream", response);
          if (response?.data && response.data.slug) {
            modalComponent.closeModal(response.data);
            this.router.navigate(["/stream", response.data.slug, "broadcast"]);
          } else {
            modalComponent.setLoading(false);
            console.error("Le slug du stream est manquant");
            // Ici vous pouvez ajouter une notification d'erreur pour l'utilisateur
          }
        },
        error: (error) => {
          modalComponent.setLoading(false);
          console.error("Erreur lors de la création du stream:", error);
          // Ici vous pouvez ajouter une notification d'erreur pour l'utilisateur
        },
      });
    });
  }

  canCreateLive(): boolean {
    return this.isAuthenticated && this.profil?.rang === "star";
  }

  handleLiveClick(): void {
    if (!this.isAuthenticated) {
      this.authModalService.openAuthModal();
      return;
    }

    if (this.profil?.rang === "star") {
      this.openCreateStreamModal();
      return;
    }

    this.router.navigate(["/streams"]);
  }
}
